import type { FormEventHandler } from "react";

import { ChartLineUpIcon, GlobeIcon } from "@phosphor-icons/react";

import { Badge, Banner, classNames, ToggleOptions } from "@alpacahq/ui/old";

import { NewWindowLink } from "~/v2/components/common";
import type { MarketDataPlans } from "../../util";
import type { PaymentSchedule } from "../types";
import { PlanBox, type PlanBoxProps } from "./PlanBox";

const DOCS_URL = "https://docs.alpaca.markets/docs/about-market-data-api";

const BASIC_ITEMS: PlanBoxProps["items"] = [
  {
    text: "US Stocks, ETFs, Options, and Crypto",
    isChecked: true,
  },
  {
    text: "9+ Years Historical Data",
    isChecked: true,
  },
  {
    text: "Real-Time Market Coverage",
    isChecked: false,
  },
  {
    text: "All US Stock Exchanges",
    isChecked: false,
  },
  {
    text: "10,000 API Calls / Min",
    isChecked: false,
  },
  {
    text: "Unlimited Symbols on WebSocket",
    isChecked: false,
  },
];

const PLUS_ITEMS: PlanBoxProps["items"] = [
  {
    text: "US Stocks, ETFs, Options, and Crypto",
    isChecked: true,
  },
  {
    text: "9+ Years Historical Data",
    isChecked: true,
  },
  {
    text: "Real-Time Market Coverage",
    isChecked: true,
  },
  {
    text: "All US Stock Exchanges",
    isChecked: true,
  },
  {
    text: "10,000 API Calls / Min",
    isChecked: true,
  },
  {
    text: "Unlimited Symbols on WebSocket",
    isChecked: true,
  },
];

interface SelectPlanPresentationProps {
  isElite: boolean;
  paymentSchedule: PaymentSchedule;
  onPaymentScheduleChange: (paymentSchedule: PaymentSchedule) => void;
  onContinue: () => void;
  plans: MarketDataPlans;
}

export const SelectPlanPresentation = ({
  isElite,
  paymentSchedule,
  onPaymentScheduleChange,
  onContinue,
  plans,
}: SelectPlanPresentationProps) => {
  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    onContinue();
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={onSubmit}>
      {!isElite ? (
        <div className="flex items-center justify-start gap-3">
          <ToggleOptions
            ariaLabel="Algo Trader Plus Payment Schedule"
            options={[
              { label: "Monthly", value: "monthly" },
              { label: "Yearly", value: "yearly" },
            ]}
            value={paymentSchedule}
            onChange={onPaymentScheduleChange}
          />
          {paymentSchedule === "yearly" && (
            <Badge variant="v2/emerald">1 Month Free</Badge>
          )}
        </div>
      ) : (
        <Banner
          variant="info"
          message="As an Elite member, you get Algo Trader Plus for free"
        />
      )}
      <div className="flex h-full w-full flex-col gap-7 sm:grid sm:grid-cols-2">
        <PlanBox
          isDisabled
          icon={<GlobeIcon size={26} />}
          title="Basic"
          price={
            <p className="m-0 p-0 font-medium text-gray-800 text-xl leading-[28px]">
              Included
            </p>
          }
          badge={
            <Badge
              variant="v2/light-blue"
              className="border-indigo-700 bg-[rgba(199,210,254,24%)] text-indigo-700"
            >
              Current Plan
            </Badge>
          }
          items={BASIC_ITEMS}
          button="Included"
        />
        <PlanBox
          isHighlighted
          onClick={onContinue}
          icon={<ChartLineUpIcon size={26} />}
          title="Algo Trader Plus"
          price={
            <p
              className={classNames(
                "m-0 p-0 font-mono text-gray-800",
                isElite && "text-gray-400",
              )}
            >
              <span
                className={classNames(
                  "font-medium font-mono text-xl",
                  isElite && "line-through",
                )}
              >
                {isElite || paymentSchedule === "monthly"
                  ? plans.monthly.displayPrice
                  : plans.yearly.displayPrice}
              </span>
              <span className="font-mono text-base">
                /{isElite || paymentSchedule === "monthly" ? "mo" : "y"}
              </span>
            </p>
          }
          badge={
            isElite || paymentSchedule === "yearly" ? (
              <Badge variant="v2/emerald">
                {isElite ? "Free Upgrade" : "1 Month Free"}
              </Badge>
            ) : undefined
          }
          items={PLUS_ITEMS}
          button="Upgrade"
        />
      </div>
      <p className="m-0 p-0 font-medium text-gray-700 text-sm">
        More Questions?{" "}
        <NewWindowLink
          to={DOCS_URL}
          hasIcon={false}
          className="inline font-medium text-indigo-600"
        >
          See our docs
        </NewWindowLink>
      </p>
    </form>
  );
};
